import React, { useState, useRef, useEffect } from "react";
import {
  FaWhatsapp,
  FaImage,
  FaVideo,
  FaMicrophone,
  FaPlay,
  FaPause,
} from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { IoPaperPlaneSharp } from "react-icons/io5";
import styled from "styled-components";
import MensagemRapida from "../Components/MensagemRapida";

/* ===================== CONFIG =========================== */
const API_URL =
  import.meta?.env?.VITE_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  "https://api.captacrm.com.br";

/* ===================== FETCH COM TIMEOUT =========================== */
function timeout(ms, reason = "timeout") {
  return new Promise((_, rej) => setTimeout(() => rej(new Error(reason)), ms));
}
async function fetchJSON(url, opts = {}, { timeoutMs = 15000 } = {}) {
  return Promise.race([
    fetch(url, opts).then(async (r) => {
      const ct = r.headers.get("content-type") || "";
      const isJSON = ct.includes("application/json");
      const data = isJSON
        ? await r.json().catch(() => ({ error: "Falha ao decodificar JSON" }))
        : await r
            .text()
            .then((t) => ({ error: t || `HTTP ${r.status}` }))
            .catch(() => ({ error: `HTTP ${r.status}` }));
      if (!r.ok) {
        const msg = data?.error || data?.message || `HTTP ${r.status}`;
        const e = new Error(msg);
        e.status = r.status;
        e.data = data;
        throw e;
      }
      return data;
    }),
    timeout(timeoutMs),
  ]);
}
async function fetchText(url, opts = {}, { timeoutMs = 15000 } = {}) {
  return Promise.race([
    fetch(url, opts).then(async (r) => {
      const t = await r.text().catch(() => "");
      if (!r.ok) {
        const e = new Error(t || `HTTP ${r.status}`);
        e.status = r.status;
        e.data = t;
        throw e;
      }
      return t;
    }),
    timeout(timeoutMs),
  ]);
}

/* ===================== UTILITÁRIOS =========================== */
function formatarData(dataStr) {
  if (!dataStr) return "--/--/----";
  const data = new Date(dataStr);
  if (isNaN(data)) return "--/--/----";
  return data.toLocaleDateString("pt-BR");
}
function getNomeUsuario(usuarios, id) {
  return usuarios?.find((u) => u.id === id)?.nome || "Não informado";
}
function somenteNumeros(v = "") {
  return String(v || "").replace(/\D/g, "");
}
function ultimos11(num = "") {
  const s = somenteNumeros(num);
  return s.slice(-11);
}
function formatarNumero(numero) {
  const s = ultimos11(numero || "");
  return s ? (s.startsWith("55") ? s : "55" + s) : "";
}
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    try {
      const r = new FileReader();
      r.onload = (e) => resolve(e.target.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    } catch (err) {
      reject(err);
    }
  });
}
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    try {
      const r = new FileReader();
      r.onload = (e) => resolve(e.target.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    } catch (err) {
      reject(err);
    }
  });
}
function absolutizeUrl(u) {
  if (!u) return u;
  if (/^(data:|https?:\/\/)/i.test(u)) return u;
  const base = (API_URL || "").replace(/\/+$/, "");
  const path = String(u).startsWith("/") ? u : `/${u}`;
  return `${base}${path}`;
}
function inferAudioMimeFrom(urlOrMime) {
  if (!urlOrMime) return "audio/webm";
  if (String(urlOrMime).includes("/")) return urlOrMime;
  const u = String(urlOrMime).toLowerCase();
  if (/_audio_webm\.bin$/.test(u)) return "audio/webm";
  if (u.includes(".mp3")) return "audio/mpeg";
  if (u.includes(".ogg")) return "audio/ogg";
  if (u.includes(".wav")) return "audio/wav";
  if (u.includes(".m4a")) return "audio/mp4";
  if (u.includes(".aac")) return "audio/aac";
  if (u.includes(".webm")) return "audio/webm";
  return "audio/webm";
}
const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

/* ========= MERGE + DEDUPE ========= */
function mergeByIdentity(list, update) {
  let found = false;
  const next = list.map((m) => {
    const same =
      (update.id && m.id === update.id) ||
      (update.msgid && m.msgid === update.msgid) ||
      (update._tempId && m._tempId === update._tempId);
    if (!same) return m;
    const changedMedia = update.midia_url && update.midia_url !== m.midia_url;
    return { ...m, ...update, _bust: changedMedia ? Date.now() : m._bust };
  });
  return { next, found };
}
function normalizeDate(m) {
  const t = new Date(m?.datahora || m?.timestamp || 0).getTime();
  return Number.isFinite(t) ? t : 0;
}
function roughlySameTime(a, b, ms = 20000) {
  return Math.abs(normalizeDate(a) - normalizeDate(b)) <= ms;
}
function isOutgoingOp(m) {
  return m?.autor === "operador" && m?.direcao === "enviada";
}
function isLocalUrl(u = "") {
  return /^blob:|^data:/i.test(String(u || ""));
}
function samePhone(a, b) {
  return ultimos11(a?.telefone || "") === ultimos11(b?.telefone || "");
}
function chooseBetter(a, b) {
  const score = (m) => {
    let s = 0;
    if (m?.msgid) s += 10;
    if (m?.id) s += 5;
    if (m?.midia_url) s += 3;
    if (m?.midia_url && !isLocalUrl(m.midia_url)) s += 5;
    if (m?.status && m.status !== "enviando") s += 2;
    if (m?.duracao_ms) s += 1;
    return s;
  };
  return score(a) >= score(b) ? a : b;
}
function dedupeMessages(list) {
  const res = [];
  const byKey = new Map();
  for (const m of list) {
    const key = m?.msgid || m?.id || null;
    if (key) {
      const idx = byKey.get(key);
      if (idx != null) {
        res[idx] = chooseBetter(res[idx], m);
        continue;
      }
      byKey.set(key, res.length);
      res.push(m);
      continue;
    }
    let merged = false;
    if (m?.tipo_midia === "audio" && isOutgoingOp(m)) {
      for (let i = res.length - 1; i >= 0 && i >= res.length - 5; i--) {
        const r = res[i];
        if (
          r?.tipo_midia === "audio" &&
          isOutgoingOp(r) &&
          samePhone(r, m) &&
          roughlySameTime(r, m, 20000) &&
          (Math.abs((r?.duracao_ms || 0) - (m?.duracao_ms || 0)) <= 1500 ||
            (!r?.duracao_ms && !m?.duracao_ms))
        ) {
          res[i] = chooseBetter(r, m);
          merged = true;
          break;
        }
      }
    }
    if (!merged) res.push(m);
  }
  return res;
}

/* ===================== STYLED COMPONENTS =========================== */
const ChatContainer = styled.div`
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 100%;
  max-width: 400px;
  background: #f0f2f5;
  border-left: 2px solid #25d366;
  box-shadow: -2px 0 16px rgba(0, 0, 0, 0.12);
  z-index: 10000;
  display: flex;
  flex-direction: column;
  font-family: "Segoe UI", Arial, sans-serif;
`;
const ChatHeader = styled.div`
  background: #f6faf8;
  color: #226c56;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  border-bottom: 1px solid #e3e6e3;
  padding: 16px 20px 8px 20px;
  font-size: 16px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 4px #0001;
  position: relative;
`;
const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;
const AvatarButton = styled.button`
  --size: 56px;
  width: var(--size);
  height: var(--size);
  border-radius: 999px;
  overflow: hidden;
  border: 2px solid #ffffff;
  outline: 1px solid #e2efe9;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  padding: 0;
  background: #e8f8f3;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: ${(p) => (p.$clickable ? "zoom-in" : "default")};
  transition: transform 0.15s ease;
  &:hover {
    transform: ${(p) => (p.$clickable ? "scale(1.03)" : "none")};
  }
`;
const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;
const AvatarFallback = styled.div`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  color: #fff;
  font-weight: 700;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-image: ${(p) =>
    `linear-gradient(135deg, ${p.$from || "#22c55e"}, ${p.$to || "#10b981"})`};
`;
const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.76);
  backdrop-filter: blur(2px);
  z-index: 100000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
`;
const LightboxInner = styled.div`
  position: relative;
  max-width: 95vw;
  max-height: 90vh;
`;
const LightboxImg = styled.img`
  max-width: 95vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  user-select: none;
  -webkit-user-drag: none;
`;
const LightboxClose = styled.button`
  position: absolute;
  top: -12px;
  right: -12px;
  width: 38px;
  height: 38px;
  border-radius: 999px;
  background: #fff;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #111827;
  cursor: pointer;
  box-shadow: 0 8px 22px rgba(255, 255, 255, 0.25);
`;

const NomeLead = styled.div`
  font-size: 19px;
  font-weight: 600;
  color: #184c3a;
`;
const BadgeWrapper = styled.div`
  position: absolute;
  top: 18px;
  right: 24px;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 8px;
`;
const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #226c56;
  font-size: 22px;
  cursor: pointer;
  font-weight: 300;
  margin-left: 10px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const DadosPequenos = styled.div`
  margin-top: 2px;
  font-size: 13px;
  color: #226c56;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-weight: 400;
`;
const ChatBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 26px 12px 18px 12px;
  background: #ece5dd;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;
const EmptyMsg = styled.div`
  color: #777;
  text-align: center;
  margin-top: 32px;
`;
const MsgBubble = styled.div`
  align-self: ${({ isSent }) => (isSent ? "flex-end" : "flex-start")};
  background: ${({ isSent }) => (isSent ? "#dcf8c6" : "#fff")};
  border-radius: 16px;
  margin-bottom: 7px;
  padding: 8px 15px;
  max-width: 270px;
  box-shadow: 0 1px 2px #0001;
  font-size: 16px;
  position: relative;
  word-break: break-word;
`;
const MsgMeta = styled.div`
  font-size: 11px;
  color: #888;
  text-align: right;
  margin-top: 2px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;
const ErrorMsg = styled.div`
  color: red;
  font-size: 12px;
`;
const ChatFooter = styled.div`
  background: #f7f7f7;
  padding: 12px 12px 10px 12px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: relative;
`;
const FooterRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 10px;
  width: 100%;
`;
const MsgTextarea = styled.textarea`
  flex: 1;
  font-size: 16px;
  border-radius: 22px;
  border: 1px solid #d6efe3;
  padding: 10px 14px;
  background: #fff;
  box-shadow: 0 1px 2px #ececec;
  resize: none;
  min-height: ${(p) => (p.$expanded ? 80 : 38)}px;
  max-height: 160px;
  outline: none;
  transition: border-color 0.2s, min-height 0.2s;
  &:focus {
    border-color: #25d366;
  }
`;
const LegendInput = styled.input`
  font-size: 15px;
  border-radius: 12px;
  border: 1px solid #d6efe3;
  padding: 7px 11px;
  background: #fff;
  width: 200px;
  margin-right: 12px;
  margin-top: 0;
  outline: none;
  transition: border-color 0.2s;
  &:focus {
    border-color: #25d366;
  }
`;
const Spinner = styled.span`
  width: 22px;
  height: 22px;
  border: 2px solid #fff;
  border-top: 2px solid #25d366;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  display: inline-block;
  @keyframes spin {
    0% {
      transform: rotate(0);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const IconBtn = styled.button`
  --size: ${(p) => p.size || 34}px;
  --shadow: 0 3px 10px rgba(0, 0, 0, 0.08);
  width: var(--size);
  height: var(--size);
  border-radius: 12px;
  border: 1px solid #e3efe9;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: var(--shadow);
  transition: transform 0.12s ease, box-shadow 0.12s ease,
    border-color 0.12s ease, background 0.12s ease, opacity 0.12s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(37, 211, 102, 0.18);
    border-color: #cfe8db;
    background: #f9fffc;
  }
  &:active {
    transform: translateY(0);
    box-shadow: var(--shadow);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: var(--shadow);
    background: #f8faf9;
  }
  svg {
    width: 18px;
    height: 18px;
    color: #25d366;
  }
  position: relative;
  overflow: hidden;
  &:after {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at var(--x) var(--y),
      rgba(37, 211, 102, 0.18),
      transparent 45%
    );
    opacity: 0;
    transition: opacity 0.25s ease;
  }
  &:active:after {
    opacity: 1;
  }
`;
const SendBtn = styled(IconBtn)`
  --size: 42px;
  border-radius: 14px;
  border: none;
  background: linear-gradient(180deg, #25d366, #20c45d);
  svg {
    color: #fff;
    width: 20px;
    height: 20px;
  }
  &:hover {
    box-shadow: 0 8px 22px rgba(37, 211, 102, 0.28);
    background: linear-gradient(180deg, #25d366, #20c45d);
  }
  &:disabled {
    background: #bfead1;
  }
`;
const MicBtn = styled(IconBtn)`
  svg {
    color: #1f9e55;
  }
`;
const MicBtnRecording = styled.button`
  --size: 36px;
  width: var(--size);
  height: var(--size);
  border-radius: 50%;
  border: none;
  background: #de3535;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 8px 22px rgba(222, 53, 53, 0.35);
  position: relative;
  isolation: isolate;
  &:before {
    content: "";
    position: absolute;
    inset: -6px;
    border-radius: 50%;
    background: rgba(222, 53, 53, 0.15);
    animation: pulse 1.2s ease-in-out infinite;
  }
  svg {
    color: #fff;
    width: 18px;
    height: 18px;
  }
  @keyframes pulse {
    0% {
      transform: scale(0.9);
      opacity: 0.7;
    }
    70% {
      transform: scale(1.15);
      opacity: 0;
    }
    100% {
      transform: scale(0.9);
      opacity: 0;
    }
  }
`;
const ActionsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  width: 100%;
`;
const ToolsToggle = styled.button`
  align-self: flex-end;
  margin-top: 2px;
  margin-bottom: -2px;
  background: transparent;
  border: none;
  color: #226c56;
  font-size: 13px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 8px;
  &:hover {
    background: #e9f7f0;
  }
`;

/* ===== Botão Finalizar (compacto, rodapé) ===== */
const FinalizarBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: ${(p) => (p.$finalizada ? "#fee2e2" : "#ffffff")};
  color: ${(p) => (p.$finalizada ? "#b91c1c" : "#374151")};
  font-size: 12px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  &:hover {
    background: ${(p) => (p.$finalizada ? "#fecaca" : "#f9fafb")};
  }
`;

/* ===== Mini Audio Player ===== */
const PlayerWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid #e3efe9;
  border-radius: 12px;
  padding: 6px 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;
const Bar = styled.div`
  position: relative;
  flex: 1;
  height: 6px;
  border-radius: 6px;
  background: #eef7f2;
  cursor: pointer;
  overflow: hidden;
  min-width: 100px;
`;
const BarInner = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${(p) => p.$pct || 0}%;
  background: #25d366;
`;
const TimeText = styled.span`
  font-size: 12px;
  color: #5c7a6d;
  min-width: 62px;
  text-align: right;
`;

/* ========== Ripple handler ========== */
function attachRipple(e) {
  const rect = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--x", `${e.clientX - rect.left}px`);
  e.currentTarget.style.setProperty("--y", `${e.clientY - rect.top}px`);
}
function formatTime(sec = 0) {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const r = String(s % 60).padStart(2, "0");
  return `${m}:${r}`;
}

/* ========== Avatar helpers ========== */
function getInitials(name = "") {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "??";
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : "";
  return (first + last).toUpperCase();
}
const PALETTE = [
  ["#6366f1", "#22d3ee"],
  ["#06b6d4", "#10b981"],
  ["#10b981", "#84cc16"],
  ["#f59e0b", "#f97316"],
  ["#f43f5e", "#ec4899"],
  ["#38bdf8", "#6366f1"],
];
function pickGradientKey(seed = "") {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

/* ========== MiniAudioPlayer ========== */
function MiniAudioPlayer({ src, type = "audio/webm", durationMs }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(durationMs ? durationMs / 1000 : 0);

  useEffect(() => {
    const a = new Audio();
    a.src = src;
    a.preload = "metadata";
    a.onloadedmetadata = () => setDur(a.duration || dur || 0);
    a.ontimeupdate = () => setCur(a.currentTime || 0);
    a.onended = () => setPlaying(false);
    audioRef.current = a;
    return () => {
      try {
        a.pause();
      } catch {}
      a.src = "";
      audioRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  async function toggle() {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      try {
        await a.play();
        setPlaying(true);
      } catch (err) {
        // Evita crash quando não houve interação do usuário ainda (NotAllowedError)
        console.warn("Audio play bloqueado pelo navegador:", err?.message);
        alert("Clique no player para iniciar o áudio.");
      }
    }
  }
  function seekByClick(e) {
    const a = audioRef.current;
    if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    a.currentTime = Math.max(0, Math.min(dur * pct, dur));
  }
  const pct = dur ? (cur / dur) * 100 : 0;

  return (
    <PlayerWrap>
      <IconBtn
        onMouseDown={attachRipple}
        onClick={toggle}
        title={playing ? "Pausar" : "Reproduzir"}
        aria-label={playing ? "Pausar áudio" : "Reproduzir áudio"}
      >
        {playing ? <FaPause /> : <FaPlay />}
      </IconBtn>
      <Bar onClick={seekByClick} title="Clique para avançar/voltar">
        <BarInner $pct={pct} />
      </Bar>
      <TimeText>
        {formatTime(cur)} / {formatTime(dur || 0)}
      </TimeText>
    </PlayerWrap>
  );
}

/* ===================== CONFIG DINÂMICA Z-API =========================== */
async function getConfigZap(empresa_id) {
  try {
    return await fetchJSON(`${API_URL}/configuracoes/zapi/${empresa_id}`);
  } catch {
    return null;
  }
}
function montarURL(zapConfig, endpoint) {
  if (!zapConfig) return "";
  return `${zapConfig.api_instancia}/instances/${zapConfig.zapi_instance_id}/token/${zapConfig.zapi_token}/${endpoint}`;
}
async function enviarMsgWhatsApp(zapConfig, numero, mensagem) {
  if (!zapConfig) throw new Error("Configuração Z-API ausente");
  const url = montarURL(zapConfig, "send-text");
  const numFormatado = formatarNumero(numero);
  const body = { phone: numFormatado, message: mensagem };
  const headers = {
    "Content-Type": "application/json",
    "Client-Token": zapConfig.client_token,
  };
  return fetchJSON(url, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
}
async function enviarAudioBase64(zapConfig, numero, base64) {
  if (!zapConfig) throw new Error("Configuração Z-API ausente");
  const url = montarURL(zapConfig, "send-audio");
  const numFormatado = formatarNumero(numero);
  const body = { phone: numFormatado, audio: base64 };
  const headers = {
    "Content-Type": "application/json",
    "Client-Token": zapConfig.client_token,
  };
  return fetchJSON(
    url,
    { method: "POST", headers, body: JSON.stringify(body) },
    { timeoutMs: 20000 }
  );
}
async function buscarFotoPerfilWhatsApp(zapConfig, numero) {
  if (!zapConfig) return "";
  const num = formatarNumero(numero);
  try {
    const data = await fetchJSON(
      `${API_URL}/whatsapp/foto-perfil?instanceId=${encodeURIComponent(
        zapConfig.zapi_instance_id
      )}&token=${encodeURIComponent(
        zapConfig.zapi_token
      )}&phone=${encodeURIComponent(num)}`
    );
    return data?.profileUrl || "";
  } catch {
    return "";
  }
}

/* ===== Envio via BACKEND (imagem/vídeo) ===== */
async function enviarImagemViaBackend({
  empresaId,
  leadId,
  telefone,
  base64,
  legenda,
  mime,
  sizeBytes,
}) {
  return fetchJSON(
    `${API_URL}/whatsapp/enviar-imagem`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        empresa_id: empresaId,
        lead_id: leadId || null,
        telefone,
        base64,
        legenda: legenda || "",
        mime_type: mime || null,
        tamanho_bytes: sizeBytes || null,
      }),
    },
    { timeoutMs: 30000 }
  );
}
async function enviarVideoViaBackend({
  empresaId,
  leadId,
  telefone,
  base64,
  legenda,
  mime,
  sizeBytes,
}) {
  return fetchJSON(
    `${API_URL}/whatsapp/enviar-video`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        empresa_id: empresaId,
        lead_id: leadId || null,
        telefone,
        base64,
        legenda,
        mime_type: mime || null,
        tamanho_bytes: sizeBytes || null,
      }),
    },
    { timeoutMs: 60000 }
  );
}

/* ===================== BACKEND/MENSAGEM/HISTÓRICO =========================== */
async function salvarMensagemBackend(payload) {
  try {
    return await fetchJSON(`${API_URL}/whatsapp/enviar`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    return { error: err.message };
  }
}
async function buscarHistoricoWhatsApp(lead_id, empresa_id) {
  try {
    return await fetchJSON(
      `${API_URL}/whatsapp/historico/lead/${lead_id}?empresa_id=${empresa_id}`
    );
  } catch {
    return [];
  }
}
async function marcarComoLidas(lead_id, empresa_id) {
  try {
    await fetchText(`${API_URL}/whatsapp/marcar-lidas`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ lead_id, empresa_id }),
    });
  } catch {}
}

/* ===================== CONVERSA FINALIZADA (OPCIONAL, com fallback) =========================== */
async function fetchConversaStatus(lead_id, empresa_id) {
  // Se a rota não existir (404), vamos sinalizar para desabilitar o recurso.
  return fetchJSON(
    `${API_URL}/whatsapp/conversa/status?empresa_id=${empresa_id}&lead_id=${lead_id}`
  );
}
async function finalizarConversa(lead_id, empresa_id) {
  return fetchJSON(`${API_URL}/whatsapp/conversa/finalizar`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ lead_id, empresa_id }),
  });
}
async function reabrirConversa(lead_id, empresa_id) {
  return fetchJSON(`${API_URL}/whatsapp/conversa/reabrir`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ lead_id, empresa_id }),
  });
}

/* ===================== ÁUDIO GRAVADOR =========================== */
function AudioGravador({ onAudioReady, enviando, disabled, suppressPreview }) {
  const [gravando, setGravando] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const [duracao, setDuracao] = useState(0);

  const pickMime = () => {
    const prefer = ["audio/mp4", "audio/webm", "audio/ogg", "audio/mpeg"];
    for (const m of prefer) {
      if (window.MediaRecorder?.isTypeSupported?.(m)) return m;
    }
    return undefined;
  };

  const iniciarGravacao = async () => {
    try {
      if (disabled || enviando) return;
      if (audioURL) {
        try {
          URL.revokeObjectURL(audioURL);
        } catch {}
        setAudioURL(null);
      }
      setGravando(true);
      setDuracao(0);
      chunksRef.current = [];

      if (!navigator.mediaDevices?.getUserMedia) {
        alert("Gravação de áudio não suportada neste navegador.");
        setGravando(false);
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      mediaRecorderRef.current = new window.MediaRecorder(
        stream,
        mime ? { mimeType: mime } : undefined
      );

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data && e.data.size) chunksRef.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const type = mime || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        const url = URL.createObjectURL(blob);
        if (!suppressPreview) setAudioURL(url);
        onAudioReady?.({
          blob,
          mime: type,
          sizeBytes: blob.size || null,
          durMs: (duracao || 0) * 1000,
          url,
        });
        stream.getTracks().forEach((t) => t.stop());
      };

      mediaRecorderRef.current.start();
      timerRef.current = setInterval(() => setDuracao((s) => s + 1), 1000);
    } catch (err) {
      console.error("Erro ao iniciar gravação:", err);
      alert("Não foi possível iniciar a gravação de áudio.");
      setGravando(false);
    }
  };

  const pararGravacao = () => {
    try {
      setGravando(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
      }
    } catch (err) {
      console.error("Erro ao parar gravação:", err);
    }
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      try {
        if (audioURL) URL.revokeObjectURL(audioURL);
      } catch {}
    };
  }, [audioURL]);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      {!gravando ? (
        <MicBtn
          onMouseDown={attachRipple}
          onClick={iniciarGravacao}
          disabled={enviando || disabled}
          title="Gravar áudio"
          aria-disabled={enviando || disabled}
        >
          <FaMicrophone />
        </MicBtn>
      ) : (
        <MicBtnRecording onClick={pararGravacao} title="Parar gravação">
          <FaMicrophone />
        </MicBtnRecording>
      )}

      {!suppressPreview && audioURL && (
        <div style={{ marginLeft: 6, minWidth: 160, flex: 1 }}>
          <MiniAudioPlayer src={audioURL} />
        </div>
      )}
    </div>
  );
}

/* ===================== COMPONENTE PRINCIPAL =========================== */
export default function LeadsWhatsApp({
  lead,
  empresaId,
  onClose,
  usuarios = [],
  dentistas = [],
  procedimentos = [],
  agendamentos = [],
}) {
  const [novaMensagem, setNovaMensagem] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [naoLidas, setNaoLidas] = useState(0);
  const [fotoPerfil, setFotoPerfil] = useState("");
  const [midiaType, setMidiaType] = useState("");
  const [midiaFile, setMidiaFile] = useState(null);
  const [legenda, setLegenda] = useState("");
  const [audioDraft, setAudioDraft] = useState(null); // {blob, url, mime, sizeBytes, durMs}
  const [audioSent, setAudioSent] = useState(false);
  const [conectado, setConectado] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [showTools, setShowTools] = useState(false);

  // Status conversa (opcional)
  const [conversaStatus, setConversaStatus] = useState("aberta"); // 'aberta' | 'finalizada'
  const [conversaFeatureAtiva, setConversaFeatureAtiva] = useState(true); // desliga se API 404

  const [midiaPreview, setMidiaPreview] = useState(null);
  function clearMidiaSelection() {
    try {
      if (midiaPreview) URL.revokeObjectURL(midiaPreview);
    } catch {}
    setMidiaPreview(null);
    setMidiaFile(null);
    setMidiaType("");
    setLegenda("");
  }
  function clearAudioDraft() {
    try {
      if (audioDraft?.url) URL.revokeObjectURL(audioDraft.url);
    } catch {}
    setAudioDraft(null);
  }
  useEffect(
    () => () => {
      try {
        if (midiaPreview) URL.revokeObjectURL(midiaPreview);
      } catch {}
      try {
        if (audioDraft?.url) URL.revokeObjectURL(audioDraft.url);
      } catch {}
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [midiaPreview, audioDraft?.url]
  );

  const chatEndRef = useRef(null);
  const sseRef = useRef(null);
  const pollRef = useRef(null);
  const sseBackoffRef = useRef(2000);
  const windowFocusedRef = useRef(true);

  const [from, to] = pickGradientKey(
    (lead?.nome_lead || "") + (fotoPerfil || "")
  );
  const initials = getInitials(lead?.nome_lead || lead?.telefone1 || "");
  const avatarClickable = Boolean(fotoPerfil);

  // auto-scroll
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    });
    return () => cancelAnimationFrame(id);
  }, [historico]);

  // carregar histórico + marcar lidas + carregar status conversa (se suportado)
  const refetchHistorico = async () => {
    if (!lead?.id || !empresaId) return;
    const h = await buscarHistoricoWhatsApp(lead.id, empresaId);
    setHistorico(Array.isArray(h) ? dedupeMessages(h) : []);
    requestAnimationFrame(() =>
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" })
    );
  };
  const refetchConversaStatus = async () => {
    if (!lead?.id || !empresaId || !conversaFeatureAtiva) return;
    try {
      const s = await fetchConversaStatus(lead.id, empresaId);
      setConversaStatus(s?.status === "finalizada" ? "finalizada" : "aberta");
    } catch (e) {
      if (e?.status === 404) {
        setConversaFeatureAtiva(false); // desliga o recurso, some o botão
        setConversaStatus("aberta");
      } else {
        console.warn("Status conversa indisponível:", e?.message || e);
      }
    }
  };
  useEffect(() => {
    const idLead = lead?.id;
    const idEmpresa = empresaId || lead?.empresa_id;
    if (!idLead || !idEmpresa) return;
    refetchHistorico();
    refetchConversaStatus();
    marcarComoLidas(idLead, idEmpresa).then(() => setNaoLidas(0));
  }, [lead?.id, empresaId]);

  // Foto perfil
  useEffect(() => {
    let alive = true;
    if (lead?.telefone1 && empresaId) {
      setFotoPerfil("");
      getConfigZap(empresaId).then((cfg) => {
        if (!alive || !cfg) return;
        buscarFotoPerfilWhatsApp(cfg, lead.telefone1).then(
          (u) => alive && setFotoPerfil(u)
        );
      });
    }
    return () => {
      alive = false;
    };
  }, [lead?.telefone1, empresaId]);

  // SSE + fallback polling
  useEffect(() => {
    if (!empresaId) return;

    function stopAll() {
      if (sseRef.current) {
        try {
          sseRef.current.close();
        } catch {}
        sseRef.current = null;
      }
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }
    stopAll();

    let canceled = false;

    function onMessageUpdatePayload(payload) {
      setHistorico((prev) => {
        const { next, found } = mergeByIdentity(prev, payload);
        const arr = found ? next : [...prev, payload];
        const deduped = dedupeMessages(arr);
        return deduped.sort(
          (a, b) =>
            new Date(a.datahora || a.timestamp || 0) -
            new Date(b.datahora || b.timestamp || 0)
        );
      });
    }

    const startSSE = () => {
      if (canceled) return;
      const url = `${API_URL}/whatsapp/realtime/subscribe?empresa_id=${empresaId}`;
      let ev;
      try {
        ev = new EventSource(url, { withCredentials: false });
      } catch (e) {
        setConectado(false);
        pollRef.current = setInterval(async () => {
          await refetchHistorico();
          await refetchConversaStatus();
        }, 5000);
        return;
      }
      sseRef.current = ev;

      ev.addEventListener("hello", () => {
        setConectado(true);
        sseBackoffRef.current = 2000;
      });
      ev.addEventListener("ping", () => {});

      ev.addEventListener("whatsapp:avatar_hint", (evt) => {
        try {
          const data = JSON.parse(evt.data || "{}");
          const telMsg = ultimos11(data.phone || "");
          const telLead = ultimos11(lead?.telefone1 || "");
          if (telMsg && telLead && telMsg === telLead && data.url)
            setFotoPerfil(data.url);
        } catch {}
      });

      ev.addEventListener("whatsapp:new_message", (evt) => {
        const data = JSON.parse(evt.data || "{}");
        if (Number(data?.empresa_id) !== Number(empresaId)) return;

        if (data.lead_id && Number(data.lead_id) !== Number(lead?.id)) {
          const telMsg = ultimos11(data.telefone || "");
          const telLead = ultimos11(lead?.telefone1 || "");
          if (telMsg && telLead && telMsg !== telLead) return;
        }

        // reabre conversa se entrou msg do cliente
        if (data?.autor === "cliente" || data?.direcao === "recebida") {
          setConversaStatus("aberta");
        }

        onMessageUpdatePayload(data);

        if (!windowFocusedRef.current) setNaoLidas((n) => n + 1);
        requestAnimationFrame(() => {
          chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
        });
      });

      ev.addEventListener("whatsapp:status_update", (evt) => {
        const data = JSON.parse(evt.data || "{}");
        onMessageUpdatePayload(data);
      });

      ev.addEventListener("whatsapp:media_ready", (evt) => {
        const data = JSON.parse(evt.data || "{}");
        onMessageUpdatePayload(data);
      });

      // sincroniza status se o backend emitir (se existir)
      ev.addEventListener("whatsapp:conversa_status", (evt) => {
        const data = JSON.parse(evt.data || "{}");
        if (Number(data?.empresa_id) !== Number(empresaId)) return;
        if (Number(data?.lead_id) !== Number(lead?.id)) return;
        if (data?.status === "finalizada" || data?.status === "aberta") {
          setConversaStatus(data.status);
          setConversaFeatureAtiva(true);
        }
      });

      ev.onerror = () => {
        setConectado(false);
        try {
          ev.close();
        } catch {}
        sseRef.current = null;

        if (pollRef.current == null) {
          pollRef.current = setInterval(async () => {
            await refetchHistorico();
            await refetchConversaStatus();
          }, 5000);
        }

        const wait = Math.min(sseBackoffRef.current, 30000);
        const t = setTimeout(() => {
          if (canceled) return;
          try {
            clearInterval(pollRef.current);
          } catch {}
          pollRef.current = null;
          startSSE();
        }, wait);
        sseBackoffRef.current = Math.min(wait * 2, 30000);
        return () => clearTimeout(t);
      };
    };

    startSSE();
    return () => {
      canceled = true;
      stopAll();
    };
  }, [empresaId, lead?.id, lead?.telefone1]);

  // monitorar foco/visibilidade
  useEffect(() => {
    const onFocus = () => {
      windowFocusedRef.current = true;
      refetchHistorico();
      refetchConversaStatus();
      if (lead?.id && empresaId) marcarComoLidas(lead.id, empresaId);
    };
    const onBlur = () => {
      windowFocusedRef.current = false;
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") onFocus();
    };
    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [empresaId, lead?.id]);

  // REFETCH rápido quando existir áudio sem midia_url
  useEffect(() => {
    const pending = historico.some(
      (m) => m.tipo_midia === "audio" && !m.midia_url
    );
    if (!pending) return;
    const t = setTimeout(refetchHistorico, 2000);
    return () => clearTimeout(t);
  }, [historico]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!lead) return null;

  /* ===== Otimista ===== */
  const pushOtimista = (msg) => {
    setHistorico((prev) => {
      const next = [...prev, msg];
      const deduped = dedupeMessages(next);
      return deduped.sort(
        (a, b) =>
          new Date(a.datahora || a.timestamp || 0) -
          new Date(b.datahora || b.timestamp || 0)
      );
    });
    requestAnimationFrame(() =>
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" })
    );
  };

  /* ===== Garante reabertura se estiver finalizada ===== */
  const ensureConversacaoAberta = async () => {
    if (!conversaFeatureAtiva) return; // recurso não disponível no backend
    if (conversaStatus === "finalizada" && lead?.id && empresaId) {
      try {
        await reabrirConversa(lead.id, empresaId);
        setConversaStatus("aberta");
      } catch (e) {
        // se 404, desliga o recurso definitivamente nesta sessão
        if (e?.status === 404) setConversaFeatureAtiva(false);
        else alert("Não foi possível reabrir a conversa agora.");
      }
    }
  };

  /* ===== Envio de TEXTO ===== */
  const handleEnviarMensagem = async () => {
    if (!novaMensagem.trim() || enviando) return;
    setEnviando(true);

    await ensureConversacaoAberta();

    const tempId = uid();
    const tempMsg = {
      _tempId: tempId,
      direcao: "enviada",
      autor: "operador",
      texto: novaMensagem,
      telefone: formatarNumero(lead.telefone1),
      datahora: new Date().toISOString(),
      status: "enviando",
      empresa_id: empresaId,
      tipo_midia: null,
    };
    pushOtimista(tempMsg);

    let resultado = null,
      msgid = null,
      statusEnvio = "erro",
      erroEnvio = null;

    try {
      const cfg = await getConfigZap(empresaId);
      resultado = await enviarMsgWhatsApp(cfg, lead.telefone1, novaMensagem);
      msgid = resultado?.messageId || resultado?.id || null;
      statusEnvio =
        resultado && (resultado.sent === true || msgid) ? "enviado" : "erro";
      if (statusEnvio === "erro")
        erroEnvio = resultado?.error || "Falha desconhecida Z-API";
    } catch {
      erroEnvio = "Erro de conexão com a Z-API";
      statusEnvio = "erro";
    }

    const saved = await salvarMensagemBackend({
      telefone: formatarNumero(lead.telefone1),
      texto: novaMensagem,
      autor: "operador",
      lead_id: lead.id,
      msgid,
      status: statusEnvio,
      direcao: "enviada",
      erro: erroEnvio,
      empresa_id: empresaId,
      tipo_midia: null,
    });

    setHistorico((prev) => {
      const mapped = prev.map((m) =>
        m._tempId === tempId
          ? {
              ...m,
              msgid: msgid || m.msgid,
              status: statusEnvio,
              id: saved?.id || m.id,
            }
          : m
      );
      const deduped = dedupeMessages(mapped);
      return deduped.sort(
        (a, b) =>
          new Date(a.datahora || a.timestamp || 0) -
          new Date(b.datahora || b.timestamp || 0)
      );
    });

    if (statusEnvio === "enviado") setNovaMensagem("");
    else alert("Erro ao enviar mensagem: " + (erroEnvio || "Tente novamente."));
    setEnviando(false);
  };

  /* ===== Mensagem rápida ===== */
  const handleEnviarMensagemInstantanea = async (texto) => {
    if (!texto.trim() || enviando) return;
    setNovaMensagem("");
    setEnviando(true);

    await ensureConversacaoAberta();

    const tempId = uid();
    pushOtimista({
      _tempId: tempId,
      direcao: "enviada",
      autor: "operador",
      texto,
      telefone: formatarNumero(lead.telefone1),
      datahora: new Date().toISOString(),
      status: "enviando",
      empresa_id: empresaId,
      tipo_midia: null,
    });

    let resultado = null,
      msgid = null,
      statusEnvio = "erro",
      erroEnvio = null;

    try {
      const cfg = await getConfigZap(empresaId);
      resultado = await enviarMsgWhatsApp(cfg, lead.telefone1, texto);
      msgid = resultado?.messageId || resultado?.id || null;
      statusEnvio =
        resultado && (resultado.sent === true || msgid) ? "enviado" : "erro";
      if (statusEnvio === "erro")
        erroEnvio = resultado?.error || "Falha desconhecida Z-API";
    } catch {
      erroEnvio = "Erro de conexão com a Z-API";
      statusEnvio = "erro";
    }

    const saved = await salvarMensagemBackend({
      telefone: formatarNumero(lead.telefone1),
      texto,
      autor: "operador",
      lead_id: lead.id,
      msgid,
      status: statusEnvio,
      direcao: "enviada",
      erro: erroEnvio,
      empresa_id: empresaId,
      tipo_midia: null,
    });

    setHistorico((prev) => {
      const mapped = prev.map((m) =>
        m._tempId === tempId
          ? {
              ...m,
              msgid: msgid || m.msgid,
              status: statusEnvio,
              id: saved?.id || m.id,
            }
          : m
      );
      const deduped = dedupeMessages(mapped);
      return deduped.sort(
        (a, b) =>
          new Date(a.datahora || a.timestamp || 0) -
          new Date(b.datahora || b.timestamp || 0)
      );
    });

    if (statusEnvio !== "enviado")
      alert("Erro ao enviar mensagem: " + (erroEnvio || "Tente novamente."));
    setEnviando(false);
  };

  /* ===== Envio de IMAGEM/VIDEO via BACKEND ===== */
  const handleEnviarMidia = async () => {
    if (!midiaFile || enviando || !midiaType) return;
    setEnviando(true);

    await ensureConversacaoAberta();

    const tempId = uid();
    const tempItem = {
      _tempId: tempId,
      direcao: "enviada",
      autor: "operador",
      texto: legenda || "",
      telefone: formatarNumero(lead.telefone1),
      datahora: new Date().toISOString(),
      status: "enviando",
      empresa_id: empresaId,
      tipo_midia: midiaType,
      midia_url: midiaPreview,
      mime_type: midiaFile?.type || null,
    };
    pushOtimista(tempItem);

    try {
      const base64 = await fileToBase64(midiaFile);
      let resp = null;

      if (midiaType === "imagem") {
        resp = await enviarImagemViaBackend({
          empresaId,
          leadId: lead?.id,
          telefone: lead?.telefone1,
          base64,
          legenda,
          mime: midiaFile?.type || null,
          sizeBytes: midiaFile?.size || null,
        });
      } else if (midiaType === "video") {
        resp = await enviarVideoViaBackend({
          empresaId,
          leadId: lead?.id,
          telefone: lead?.telefone1,
          base64,
          legenda,
          mime: midiaFile?.type || null,
          sizeBytes: midiaFile?.size || null,
        });
      }

      setHistorico((prev) => {
        const mapped = prev.map((m) =>
          m._tempId === tempId
            ? {
                ...m,
                id: resp?.id || m.id,
                msgid: resp?.msgid || m.msgid,
                status: resp ? "enviado" : "erro",
                midia_url: resp?.midia_url || m.midia_url,
              }
            : m
        );
        const deduped = dedupeMessages(mapped);
        return deduped.sort(
          (a, b) =>
            new Date(a.datahora || a.timestamp || 0) -
            new Date(b.datahora || b.timestamp || 0)
        );
      });

      clearMidiaSelection();
      setShowTools(false);
    } catch (err) {
      setHistorico((prev) =>
        prev.map((m) =>
          m._tempId === tempId
            ? { ...m, status: "erro", erro: err?.message }
            : m
        )
      );
      alert("Erro ao enviar mídia: " + (err?.message || "Tente novamente."));
    } finally {
      setEnviando(false);
    }
  };

  /* ===== Envio do ÁUDIO ===== */
  const handleEnviarAudioDraft = async () => {
    if (!audioDraft?.blob || enviando) return;
    setEnviando(true);

    await ensureConversacaoAberta();

    const { blob, mime, sizeBytes, durMs, url } = audioDraft;

    const tempId = uid();
    pushOtimista({
      _tempId: tempId,
      direcao: "enviada",
      autor: "operador",
      texto: "",
      telefone: formatarNumero(lead.telefone1),
      datahora: new Date().toISOString(),
      status: "enviando",
      empresa_id: empresaId,
      tipo_midia: "audio",
      midia_url: url,
      mime_type: mime || "audio/webm",
      duracao_ms: durMs || null,
    });

    try {
      const base64 = await blobToBase64(blob);

      let resultado = null,
        msgid = null,
        statusEnvio = "erro",
        erroEnvio = null;
      try {
        const cfg = await getConfigZap(empresaId);
        resultado = await enviarAudioBase64(cfg, lead.telefone1, base64);
        msgid = resultado?.messageId || resultado?.id || null;
        statusEnvio =
          resultado && (resultado.sent === true || msgid) ? "enviado" : "erro";
        if (statusEnvio === "erro")
          erroEnvio = resultado?.error || "Falha desconhecida Z-API";
      } catch {
        erroEnvio = "Erro de conexão com a Z-API";
        statusEnvio = "erro";
      }

      const saved = await salvarMensagemBackend({
        telefone: formatarNumero(lead.telefone1),
        texto: "",
        autor: "operador",
        lead_id: lead.id,
        msgid,
        status: statusEnvio,
        direcao: "enviada",
        erro: erroEnvio,
        empresa_id: empresaId,
        tipo_midia: "audio",
        base64,
        legenda: "",
        mime_type: mime || "audio/webm",
        tamanho_bytes: sizeBytes || null,
        duracao_ms: durMs || null,
      });

      setHistorico((prev) => {
        const mapped = prev.map((m) =>
          m._tempId === tempId
            ? {
                ...m,
                msgid: msgid || m.msgid,
                status: statusEnvio,
                id: saved?.id || m.id,
              }
            : m
        );
        const deduped = dedupeMessages(mapped);
        return deduped.sort(
          (a, b) =>
            new Date(a.datahora || a.timestamp || 0) -
            new Date(b.datahora || b.timestamp || 0)
        );
      });

      if (statusEnvio === "enviado") {
        clearAudioDraft();
        setAudioSent(true);
        setShowTools(false);
        setTimeout(() => setAudioSent(false), 2500);
      } else {
        alert("Erro ao enviar áudio: " + (erroEnvio || "Tente novamente."));
      }
    } finally {
      setEnviando(false);
    }
  };

  const handleAudioReady = ({ blob, mime, sizeBytes, durMs, url }) => {
    if (!blob) return;
    setAudioDraft({ blob, mime, sizeBytes, durMs, url });
    setShowTools(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (audioDraft) handleEnviarAudioDraft();
      else if (midiaFile) handleEnviarMidia();
      else handleEnviarMensagem();
    }
  };

  const canSend = enviando
    ? false
    : !!audioDraft || !!midiaFile || !!novaMensagem.trim();

  const toggleFinalizar = async () => {
    if (!conversaFeatureAtiva) return; // se o backend não tem a rota, não faz nada
    if (!lead?.id || !empresaId) return;
    try {
      if (conversaStatus === "finalizada") {
        await reabrirConversa(lead.id, empresaId);
        setConversaStatus("aberta");
      } else {
        await finalizarConversa(lead.id, empresaId);
        setConversaStatus("finalizada");
      }
    } catch (e) {
      if (e?.status === 404) {
        setConversaFeatureAtiva(false); // desativa botão definitivamente
        alert("Este servidor não possui o recurso de finalizar conversa.");
      } else {
        const msg =
          e?.data?.error ||
          e?.message ||
          "Falha ao alterar status da conversa.";
        alert("Falha ao alterar status da conversa: " + msg);
      }
    }
  };

  return (
    <ChatContainer>
      <BadgeWrapper>
        <div
          title={
            conectado ? "Tempo real conectado" : "Conexão instável (fallback)"
          }
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: conectado ? "#22c55e" : "#f59e0b",
            boxShadow: conectado ? "0 0 6px #22c55ea6" : "0 0 6px #f59e0ba6",
          }}
        />
        <div style={{ position: "relative", display: "inline-block" }}>
          <FaWhatsapp size={28} color="#25d366" />
          {naoLidas > 0 && (
            <span
              style={{
                position: "absolute",
                top: -5,
                right: -8,
                background: "#de3535",
                color: "white",
                borderRadius: "50%",
                padding: "3px 7px",
                fontSize: "13px",
                fontWeight: "bold",
                boxShadow: "0 0 6px #de3535a6",
              }}
            >
              {naoLidas}
            </span>
          )}
        </div>
        <CloseButton onClick={onClose} title="Fechar" aria-label="Fechar chat">
          <IoMdClose />
        </CloseButton>
      </BadgeWrapper>

      <ChatHeader>
        <TopRow>
          <AvatarButton
            type="button"
            onClick={() => avatarClickable && setAvatarOpen(true)}
            $clickable={avatarClickable}
            aria-label={avatarClickable ? "Ver foto do contato" : "Avatar"}
            title={avatarClickable ? "Clique para ampliar" : "Avatar"}
          >
            {fotoPerfil ? (
              <AvatarImg
                src={fotoPerfil}
                alt="Foto WhatsApp"
                loading="lazy"
                decoding="async"
                referrerPolicy="no-referrer"
                crossOrigin="anonymous"
                onError={() => setFotoPerfil("")}
              />
            ) : (
              <AvatarFallback $from={from} $to={to}>
                {initials}
              </AvatarFallback>
            )}
          </AvatarButton>

          <NomeLead>{lead?.nome_lead || "-"}</NomeLead>
        </TopRow>

        <DadosPequenos>
          <span>
            <b>Telefone:</b> {lead?.telefone1 || "--"}
          </span>
          <span>
            <b>Operador:</b> {getNomeUsuario(usuarios, lead?.id_usuario)}
          </span>
          <span>
            <b>Captador:</b>{" "}
            {getNomeUsuario(usuarios, lead?.usuario_criador_id)}
          </span>
          <span>
            <b>Criada em:</b>{" "}
            {formatarData(lead?.criado_em || lead?.data_criacao)}
          </span>
        </DadosPequenos>
      </ChatHeader>

      <ChatBody>
        {historico.length === 0 && <EmptyMsg>Nenhuma mensagem ainda.</EmptyMsg>}

        {historico.map((msg) => {
          const isAudio = msg.tipo_midia === "audio";
          const isImg = msg.tipo_midia === "imagem";
          const isVid = msg.tipo_midia === "video";

          const srcRaw = msg.midia_url || msg.base64 || null;
          const base = absolutizeUrl(srcRaw);
          const src = base
            ? base +
              (msg._bust
                ? (base.includes("?") ? "&" : "?") + "v=" + msg._bust
                : "")
            : null;

          const audioType = inferAudioMimeFrom(msg.mime_type || src);

          return (
            <MsgBubble
              key={
                msg.id ||
                msg.msgid ||
                msg._tempId ||
                `${msg.telefone}-${msg.datahora}-${msg.tipo_midia || "txt"}`
              }
              isSent={msg.direcao === "enviada"}
              title={
                msg.datahora
                  ? new Date(msg.datahora).toLocaleString("pt-BR")
                  : ""
              }
            >
              {isImg && src ? (
                <div>
                  <img
                    src={src}
                    alt="imagem"
                    style={{ maxWidth: "100%", borderRadius: "8px" }}
                  />
                  {!!msg.texto && (
                    <div style={{ marginTop: 4 }}>{msg.texto}</div>
                  )}
                </div>
              ) : isVid && src ? (
                <div>
                  <video
                    src={src}
                    controls
                    style={{ maxWidth: "100%", borderRadius: "8px" }}
                  />
                  {!!msg.texto && (
                    <div style={{ marginTop: 4 }}>{msg.texto}</div>
                  )}
                </div>
              ) : isAudio && src ? (
                <MiniAudioPlayer
                  src={src}
                  type={audioType}
                  durationMs={msg.duracao_ms}
                />
              ) : (
                msg.texto
              )}

              <MsgMeta>
                <span style={{ marginRight: 6 }}>
                  {msg.datahora
                    ? new Date(msg.datahora).toLocaleTimeString("pt-BR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : ""}
                </span>
                <span>{msg.autor === "operador" ? "Você" : "Cliente"}</span>
                {msg.direcao === "enviada" && (
                  <span style={{ marginLeft: 6 }}>
                    {msg.status === "enviando" && (
                      <span title="Enviando">…</span>
                    )}
                    {msg.status === "enviado" && <span title="Enviado">✓</span>}
                    {msg.status === "delivered" && (
                      <span title="Entregue">✓✓</span>
                    )}
                    {msg.status === "read" && (
                      <span title="Lida" style={{ color: "#25d366" }}>
                        ✓✓
                      </span>
                    )}
                    {msg.status === "erro" && (
                      <span style={{ color: "red" }} title="Erro ao enviar">
                        ✗
                      </span>
                    )}
                  </span>
                )}
              </MsgMeta>
              {msg.status === "erro" && <ErrorMsg>Erro: {msg.erro}</ErrorMsg>}
            </MsgBubble>
          );
        })}

        {/* etiqueta da conversa finalizada (apenas informativa) */}
        {conversaFeatureAtiva && conversaStatus === "finalizada" && (
          <div
            style={{
              alignSelf: "center",
              color: "#b91c1c",
              background: "#fee2e2",
              border: "1px dashed #fecaca",
              padding: "6px 12px",
              borderRadius: 999,
              fontSize: 12,
              marginTop: 6,
            }}
            title="Esta conversa foi finalizada. Ao enviar ou receber nova mensagem ela reabre."
          >
            — conversa finalizada —
          </div>
        )}

        <div ref={chatEndRef} />
      </ChatBody>

      <ChatFooter>
        {/* Linha principal: textarea OU prévia (imagem/vídeo/áudio) + botão Enviar */}
        <FooterRow style={{ marginBottom: 2 }}>
          {!midiaFile && !audioDraft ? (
            <>
              <MsgTextarea
                placeholder={
                  conversaFeatureAtiva && conversaStatus === "finalizada"
                    ? "Conversa finalizada — ao enviar você reabre."
                    : "Digite uma mensagem"
                }
                value={novaMensagem}
                disabled={enviando}
                onChange={(e) => setNovaMensagem(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={showTools ? 2 : 4}
                $expanded={!showTools}
              />
              <SendBtn
                onMouseDown={attachRipple}
                onClick={handleEnviarMensagem}
                disabled={!canSend || !!midiaFile || !!audioDraft}
                title="Enviar mensagem"
                aria-label="Enviar mensagem"
              >
                {enviando ? <Spinner /> : <IoPaperPlaneSharp />}
              </SendBtn>
            </>
          ) : (
            <>
              {/* Prévia de mídia/áudio ANTES DE ENVIAR */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  background: "#fff",
                  border: "1px solid #e3efe9",
                  borderRadius: 12,
                  padding: 8,
                }}
              >
                {/* IMAGEM / VÍDEO */}
                {midiaFile && (
                  <>
                    <div style={{ flexShrink: 0 }}>
                      {midiaType === "imagem" ? (
                        <img
                          src={midiaPreview}
                          alt="Prévia"
                          style={{
                            width: 80,
                            height: 80,
                            objectFit: "cover",
                            borderRadius: 8,
                          }}
                        />
                      ) : (
                        <video
                          src={midiaPreview}
                          controls
                          style={{ width: 140, maxHeight: 90, borderRadius: 8 }}
                        />
                      )}
                    </div>
                    <LegendInput
                      type="text"
                      placeholder="Legenda (opcional)"
                      value={legenda}
                      onChange={(e) => setLegenda(e.target.value)}
                      disabled={enviando}
                      style={{ flex: 1 }}
                    />
                    <IconBtn
                      onMouseDown={attachRipple}
                      onClick={clearMidiaSelection}
                      title="Cancelar"
                      aria-label="Cancelar prévia"
                    >
                      <IoMdClose />
                    </IconBtn>
                  </>
                )}

                {/* ÁUDIO */}
                {audioDraft && (
                  <>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <MiniAudioPlayer
                        src={audioDraft.url}
                        type={audioDraft.mime}
                        durationMs={audioDraft.durMs}
                      />
                    </div>
                    <IconBtn
                      onMouseDown={attachRipple}
                      onClick={clearAudioDraft}
                      title="Cancelar"
                      aria-label="Cancelar prévia de áudio"
                    >
                      <IoMdClose />
                    </IconBtn>
                  </>
                )}
              </div>

              <SendBtn
                onMouseDown={attachRipple}
                onClick={
                  audioDraft ? handleEnviarAudioDraft : handleEnviarMidia
                }
                disabled={enviando}
                title="Enviar"
                aria-label="Enviar"
              >
                {enviando ? <Spinner /> : <IoPaperPlaneSharp />}
              </SendBtn>
            </>
          )}
        </FooterRow>

        {/* Linha de ações inferiores: anexos + finalizar */}
        <FooterRow
          style={{ alignItems: "center", justifyContent: "space-between" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <ToolsToggle
              onClick={() => setShowTools((v) => !v)}
              title={
                showTools
                  ? "Ocultar anexos/comandos"
                  : "Mostrar anexos/comandos"
              }
            >
              {showTools ? "▲ Ocultar anexos/comandos" : "▼ Anexos e comandos"}
            </ToolsToggle>
          </div>

          {/* Botão Finalizar no rodapé (discreto) */}
          {conversaFeatureAtiva && (
            <FinalizarBtn
              onClick={toggleFinalizar}
              $finalizada={conversaStatus === "finalizada"}
              title={
                conversaStatus === "finalizada"
                  ? "Reabrir conversa"
                  : "Finalizar conversa (não envia nada ao cliente)"
              }
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background:
                    conversaStatus === "finalizada" ? "#ef4444" : "#10b981",
                  border: "1px solid #e5e7eb",
                }}
              />
              {conversaStatus === "finalizada"
                ? "Reabrir conversa"
                : "Finalizar conversa"}
            </FinalizarBtn>
          )}
        </FooterRow>

        {/* Ações (recolhíveis) */}
        {showTools && (
          <>
            <ActionsBar style={{ marginTop: 6, marginBottom: 8 }}>
              <IconBtn
                onMouseDown={attachRipple}
                as="label"
                title="Anexar imagem"
                disabled={enviando || !!audioDraft || !!midiaFile}
                aria-disabled={enviando || !!audioDraft || !!midiaFile}
              >
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      setMidiaType("imagem");
                      setMidiaFile(f);
                      setLegenda("");
                      try {
                        setMidiaPreview(URL.createObjectURL(f));
                      } catch {}
                    }
                  }}
                  disabled={enviando || !!audioDraft || !!midiaFile}
                />
                <FaImage />
              </IconBtn>

              <IconBtn
                onMouseDown={attachRipple}
                as="label"
                title="Anexar vídeo"
                disabled={enviando || !!audioDraft || !!midiaFile}
                aria-disabled={enviando || !!audioDraft || !!midiaFile}
              >
                <input
                  type="file"
                  accept="video/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      setMidiaType("video");
                      setMidiaFile(f);
                      setLegenda("");
                      try {
                        setMidiaPreview(URL.createObjectURL(f));
                      } catch {}
                    }
                  }}
                  disabled={enviando || !!audioDraft || !!midiaFile}
                />
                <FaVideo />
              </IconBtn>

              <AudioGravador
                onAudioReady={handleAudioReady}
                enviando={enviando}
                disabled={!!audioDraft || !!midiaFile}
                suppressPreview={!!audioDraft}
              />
            </ActionsBar>

            {/* Mensagens rápidas */}
            <div style={{ width: "100%", marginTop: 6 }}>
              <MensagemRapida
                empresaId={empresaId}
                leadSelecionada={lead}
                usuarios={usuarios}
                dentistas={dentistas}
                procedimentos={procedimentos}
                agendamentos={agendamentos}
                onSeleciona={handleEnviarMensagemInstantanea}
                onSelecionar={handleEnviarMensagemInstantanea}
              />
            </div>
          </>
        )}

        {/* aviso áudio */}
        {audioSent && (
          <div
            style={{
              position: "absolute",
              top: "-34px",
              right: "18px",
              background: "#25d366",
              color: "#fff",
              padding: "7px 20px",
              borderRadius: "20px",
              fontWeight: "bold",
              fontSize: "15px",
              boxShadow: "0 2px 8px #25d36644",
              transition: "opacity 0.5s",
            }}
          >
            Áudio enviado!
          </div>
        )}
      </ChatFooter>

      {/* Lightbox do Avatar */}
      {avatarOpen && fotoPerfil && (
        <LightboxOverlay onClick={() => setAvatarOpen(false)}>
          <LightboxInner onClick={(e) => e.stopPropagation()}>
            <LightboxImg
              src={fotoPerfil}
              alt={lead?.nome_lead || "Avatar grande"}
              draggable={false}
            />
            <LightboxClose
              onClick={() => setAvatarOpen(false)}
              aria-label="Fechar visualização"
              title="Fechar (Esc)"
            >
              <IoMdClose />
            </LightboxClose>
          </LightboxInner>
        </LightboxOverlay>
      )}
    </ChatContainer>
  );
}
